/**
 * Os instantes do backend, lidos e devolvidos no fuso de quem olha.
 *
 * O banco guarda UTC desde 27/09/2026, e o backend serializa `LocalDateTime` SEM fuso
 * (`2026-10-15T12:00:00`). O painel inteiro le data por fatia de string - o que e o certo
 * para uma hora de PAREDE, e errado para um instante em UTC: sem esta camada, todo horario
 * sairia 3h adiantado em Brasilia (4h em Manaus).
 *
 * A conversao acontece na BORDA, e so nela: cada `*.api.ts` passa os instantes que recebe
 * por `fromBackend`, que devolve a hora de parede de quem olha no MESMO formato
 * (`yyyy-MM-ddTHH:mm:ss`). Dali para dentro nada muda - os formatadores e os agrupamentos
 * por fatia continuam valendo. O caminho de volta e `toBackend`: o limite de um dia ou a
 * hora de um balde que o painel manda ao servidor saem em UTC, como o servidor os le.
 *
 * Data de CALENDARIO (`2026-10-15`, o nascimento, o dia de uma serie) nao e instante e nao
 * passa por aqui.
 *
 * `BackendTime.utc` e escrito pelo renderer no arranque (`VITE_DATAS_EM_UTC`), como o
 * `baseUrl` do `ApiClient`: a apresentacao nao conhece o Vite. Desligado, as duas funcoes
 * sao identidade - e o banco local de quem desenvolve sobre um dump anterior a virada.
 */
export const BackendTime = {
    utc: false
};
/** O fuso de quem esta olhando: vai ao servidor no `X-Timezone` e decide o dia de cada instante. */
export function viewerTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
const HAS_ZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;
const CALENDAR_DAY = /^\d{4}-\d{2}-\d{2}$/;
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/;
/**
 * Um instante do backend como hora de parede de quem olha (`yyyy-MM-ddTHH:mm:ss`).
 *
 * Vazio, data de calendario e texto que ja traz fuso passam como estao: nenhum dos tres e
 * um `LocalDateTime` em UTC. O tipo volta o mesmo que entrou - campo opcional continua
 * opcional, nulo continua nulo.
 */
export function fromBackend(value, timeZone = viewerTimeZone()) {
    if (!value || !BackendTime.utc || CALENDAR_DAY.test(value) || HAS_ZONE.test(value)) {
        return value;
    }
    const parts = WALL_CLOCK.exec(value);
    if (!parts) {
        return value;
    }
    const [, y, mo, d, h, mi, s] = parts;
    const instant = Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s ?? 0));
    return wallClockOf(instant, timeZone);
}
/**
 * O caminho de volta: uma hora de parede de quem olha como o instante que o servidor le (UTC).
 *
 * Usado no que o painel MANDA - o limite de um dia, a hora de um balde. O dia `2026-09-26`
 * em Brasilia comeca as `03:00` UTC; mandar `T00:00:00` cru faria o servidor ler o dia 25.
 */
export function toBackend(value, timeZone = viewerTimeZone()) {
    if (!value || !BackendTime.utc || HAS_ZONE.test(value)) {
        return value;
    }
    const local = CALENDAR_DAY.test(value) ? `${value}T00:00:00` : value;
    const parts = WALL_CLOCK.exec(local);
    if (!parts) {
        return value;
    }
    const [, y, mo, d, h, mi, s] = parts;
    const asIfUtc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s ?? 0));
    // O deslocamento depende do proprio instante (horario de verao): uma volta para acertar.
    let instant = asIfUtc - offsetOf(asIfUtc, timeZone);
    instant = asIfUtc - offsetOf(instant, timeZone);
    return new Date(instant).toISOString().slice(0, 19);
}
/** Quantos milissegundos o fuso esta a frente do UTC naquele instante (Brasilia: -3h). */
function offsetOf(instant, timeZone) {
    const wall = wallClockOf(instant, timeZone);
    const [, y, mo, d, h, mi, s] = WALL_CLOCK.exec(wall);
    return Date.UTC(+y, +mo - 1, +d, +h, +mi, +(s ?? 0)) - instant;
}
function wallClockOf(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(new Date(instant));
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
}
//# sourceMappingURL=instant.js.map